<template>
  <div class="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
    <CompanyAppLayout>
      <!-- Page Header -->
      <div class="pt-8 pb-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-bold text-white mb-2">Job Management</h1>
            <p class="text-slate-400">Manage your posted jobs and review applications</p>
          </div>
          <router-link to="/company/post-job" class="btn-primary">
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
            </svg>
            Post New Job
          </router-link>
        </div>
      </div>

      <!-- Stats Cards -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-emerald-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Active Jobs</p>
            <p class="text-3xl font-bold text-white">{{ jobStats.activeJobs }}</p>
            <p class="text-sm text-emerald-400">currently posted</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-cyan-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-cyan-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Total Applications</p>
            <p class="text-3xl font-bold text-white">{{ jobStats.totalApplications }}</p>
            <p class="text-sm text-cyan-400">this month</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-amber-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Hired Contractors</p>
            <p class="text-3xl font-bold text-white">{{ jobStats.hiredContractors }}</p>
            <p class="text-sm text-amber-400">this year</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-purple-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Success Rate</p>
            <p class="text-3xl font-bold text-white">{{ jobStats.successRate }}%</p>
            <p class="text-sm text-purple-400">job completion</p>
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="mb-8">
        <div class="border-b border-slate-700">
          <nav class="flex space-x-8">
            <button 
              @click="activeTab = 'jobs'"
              :class="[
                'py-2 px-1 border-b-2 font-medium text-sm transition-colors',
                activeTab === 'jobs' 
                  ? 'border-emerald-500 text-emerald-400' 
                  : 'border-transparent text-slate-400 hover:text-slate-300'
              ]"
            >
              Posted Jobs
            </button>
            <button 
              @click="activeTab = 'applications'"
              :class="[
                'py-2 px-1 border-b-2 font-medium text-sm transition-colors',
                activeTab === 'applications' 
                  ? 'border-emerald-500 text-emerald-400' 
                  : 'border-transparent text-slate-400 hover:text-slate-300'
              ]"
            >
              Applications
            </button>
          </nav>
        </div>
      </div>

      <!-- Jobs Tab -->
      <div v-if="activeTab === 'jobs'" class="space-y-6">
        <div class="flex items-center justify-between">
          <h2 class="text-xl font-semibold text-white">Your Posted Jobs</h2>
          <div class="flex items-center space-x-4">
            <select v-model="jobFilter" class="form-select">
              <option value="all">All Jobs</option>
              <option value="active">Active</option>
              <option value="filled">Filled</option>
              <option value="expired">Expired</option>
            </select>
            <input 
              v-model="searchQuery" 
              type="text" 
              placeholder="Search jobs..."
              class="form-input"
            />
          </div>
        </div>

        <div v-if="isLoading" class="flex justify-center py-12">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
        </div>

        <div v-else-if="filteredJobs.length === 0" class="text-center py-12">
          <div class="w-16 h-16 bg-slate-700/50 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
          </div>
          <h3 class="text-lg font-medium text-white mb-2">No jobs found</h3>
          <p class="text-slate-400 mb-4">Get started by posting your first job</p>
          <router-link to="/company/post-job" class="btn-primary">
            Post Your First Job
          </router-link>
        </div>

        <div v-else class="grid gap-6">
          <div 
            v-for="job in filteredJobs" 
            :key="job.id"
            class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 hover:bg-slate-800/70 transition-colors"
          >
            <div class="flex items-start justify-between mb-4">
              <div>
                <h3 class="text-xl font-semibold text-white mb-2">{{ job.title }}</h3>
                <p class="text-slate-400 mb-3">{{ job.description }}</p>
                <div class="flex items-center space-x-6 text-sm">
                  <span class="flex items-center text-slate-300">
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                    </svg>
                    {{ job.location }}
                  </span>
                  <span class="flex items-center text-slate-300">
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"/>
                    </svg>
                    ${{ job.salary_min.toLocaleString() }} - ${{ job.salary_max.toLocaleString() }}
                  </span>
                  <span class="flex items-center text-slate-300">
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                    </svg>
                    {{ job.applications_count }} applications
                  </span>
                </div>
              </div>
              <div class="flex items-center space-x-2">
                <span :class="[
                  'px-3 py-1 rounded-full text-xs font-medium',
                  job.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' :
                  job.status === 'filled' ? 'bg-blue-500/20 text-blue-400' :
                  'bg-slate-500/20 text-slate-400'
                ]">
                  {{ job.status }}
                </span>
              </div>
            </div>
            
            <div class="flex items-center justify-between pt-4 border-t border-slate-700/50">
              <div class="text-sm text-slate-400">
                Posted {{ formatDate(job.posted_date) }}
              </div>
              <div class="flex items-center space-x-3">
                <button @click="viewApplications(job.id)" class="btn-secondary">
                  View Applications
                </button>
                <button @click="editJob(job.id)" class="btn-primary">
                  Edit Job
                </button>
                <button @click="deleteJob(job)" class="btn-danger">
                  {{ Number(job.applications_count || 0) > 0 ? 'Archive' : 'Delete' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Applications Tab -->
      <div v-if="activeTab === 'applications'" class="space-y-6">
        <div class="flex items-center justify-between">
          <h2 class="text-xl font-semibold text-white">Job Applications</h2>
          <div class="flex items-center space-x-4">
            <select v-model="applicationFilter" class="form-select">
              <option value="all">All Applications</option>
              <option value="pending">Pending</option>
              <option value="reviewed">Reviewed</option>
              <option value="shortlisted">Shortlisted</option>
              <option value="rejected">Rejected</option>
              <option value="hired">Hired</option>
            </select>
          </div>
        </div>

        <div v-if="isLoading" class="flex justify-center py-12">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
        </div>

        <div v-else-if="filteredApplications.length === 0" class="text-center py-12">
          <div class="w-16 h-16 bg-slate-700/50 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
          </div>
          <h3 class="text-lg font-medium text-white mb-2">No applications yet</h3>
          <p class="text-slate-400">Applications will appear here when contractors apply to your jobs</p>
        </div>

        <div v-else class="grid gap-6">
          <div 
            v-for="application in filteredApplications" 
            :key="application.id"
            class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6"
          >
            <div class="flex items-start justify-between mb-4">
              <div class="flex items-start space-x-4">
                <div class="w-12 h-12 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center">
                  <span class="text-white font-semibold">{{ application.contractor_name.charAt(0) }}</span>
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-white mb-1">{{ application.contractor_name }}</h3>
                  <p class="text-slate-400 mb-2">{{ application.job_title }}</p>
                  <div class="flex items-center space-x-4 text-sm">
                    <span class="text-slate-300">{{ application.contractor_location }}</span>
                    <span class="text-slate-300">{{ application.contractor_experience }} years experience</span>
                  </div>
                </div>
              </div>
              <div class="flex items-center space-x-2">
                <span :class="[
                  'px-3 py-1 rounded-full text-xs font-medium',
                  application.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                  application.status === 'reviewed' ? 'bg-blue-500/20 text-blue-400' :
                  application.status === 'shortlisted' ? 'bg-purple-500/20 text-purple-400' :
                  application.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                  'bg-emerald-500/20 text-emerald-400'
                ]">
                  {{ application.status }}
                </span>
              </div>
            </div>
            
            <div class="mb-4">
              <p class="text-slate-300 text-sm">{{ application.cover_letter }}</p>
            </div>
            
            <div class="flex items-center justify-between pt-4 border-t border-slate-700/50">
              <div class="text-sm text-slate-400">
                Applied {{ formatDate(application.applied_at) }}
              </div>
              <div class="flex items-center space-x-3">
                <button @click="viewContractorProfile(application.contractor_id)" class="btn-secondary">
                  View Profile
                </button>
                <button @click="reviewApplication(application.id)" class="btn-primary">
                  Review
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </CompanyAppLayout>
  </div>
</template>

<script setup lang="ts">
import CompanyAppLayout from '@/components/layout/CompanyAppLayout.vue'
import { useUserStore } from '@/stores/userStore'
import { computed, onMounted, ref } from 'vue'

const userStore = useUserStore()

// Reactive data
const isLoading = ref(false)
const activeTab = ref('jobs')
const jobFilter = ref('all')
const applicationFilter = ref('all')
const searchQuery = ref('')

const jobStats = ref({
  activeJobs: 0,
  totalApplications: 0,
  hiredContractors: 0,
  successRate: 0
})

const jobs = ref([])
const applications = ref([])

// Computed properties
const filteredJobs = computed(() => {
  let filtered = jobs.value
  
  if (jobFilter.value !== 'all') {
    filtered = filtered.filter(job => job.status === jobFilter.value)
  }
  
  if (searchQuery.value) {
    filtered = filtered.filter(job => 
      job.title.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      job.description.toLowerCase().includes(searchQuery.value.toLowerCase())
    )
  }
  
  return filtered
})

const filteredApplications = computed(() => {
  let filtered = applications.value
  
  if (applicationFilter.value !== 'all') {
    filtered = filtered.filter(app => app.status === applicationFilter.value)
  }
  
  return filtered
})

// Methods
const fetchJobStats = async () => {
  try {
    const response = await fetch('/api/company/job-stats', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      jobStats.value = data
    }
  } catch (error) {
    console.error('Error fetching job stats:', error)
  }
}

const fetchJobs = async () => {
  try {
    isLoading.value = true
    const response = await fetch('/api/company/jobs', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      jobs.value = data.jobs || []
    }
  } catch (error) {
    console.error('Error fetching jobs:', error)
  } finally {
    isLoading.value = false
  }
}

const fetchApplications = async () => {
  try {
    isLoading.value = true
    const response = await fetch('/api/company/applications', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      applications.value = data.applications || []
    }
  } catch (error) {
    console.error('Error fetching applications:', error)
  } finally {
    isLoading.value = false
  }
}

const viewApplications = (jobId: number) => {
  activeTab.value = 'applications'
  // Filter applications for this specific job
}

const editJob = (jobId: number) => {
  // Navigate to edit job page
}

const deleteJob = async (job: any) => {
  const archive = Number(job.applications_count || 0) > 0
  if (!window.confirm(`${archive ? 'Archive' : 'Delete'} “${job.title}”?${archive ? ' Applications will be retained.' : ' This cannot be undone.'}`)) return
  try {
    const response = await fetch(`/api/company/jobs/${job.id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(data.error || 'Action failed')
    await fetchJobs()
    await fetchJobStats()
  } catch (error) {
    console.error('Error deleting job:', error)
  }
}

const viewContractorProfile = (contractorId: number) => {
  // Navigate to contractor profile
}

const reviewApplication = (applicationId: number) => {
  // Open review modal
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString()
}

// Lifecycle
onMounted(async () => {
  await Promise.all([
    fetchJobStats(),
    fetchJobs(),
    fetchApplications()
  ])
})
</script>

<style scoped>
.stat-card {
  @apply bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6;
}

.btn-primary {
  @apply px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition-colors flex items-center;
}

.btn-secondary {
  @apply px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 font-medium rounded-lg transition-colors;
}

.btn-danger {
  @apply px-4 py-2 bg-red-500/15 hover:bg-red-500/25 text-red-300 font-medium rounded-lg transition-colors;
}

.form-select, .form-input {
  @apply bg-slate-800/50 border border-slate-700/50 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent;
}

.form-select {
  @apply min-w-[120px];
}

.form-input {
  @apply min-w-[200px];
}
</style>
